import { msg, Trans } from '@lingui/macro'

import * as React from 'react'
import { Controller, FieldError, useFormContext, ValidationValue } from 'react-hook-form'

import { objectEntries } from '~logic/helpers/objectUtils/object'
import { useTrans } from '~logic/i18n/useTrans'
import { AnimatedHeight } from '~ui/animatedHeight/AnimatedHeight'
import { InputControl } from '~ui/components/formElements/accessories/InputControl'
import { InputLabel } from '~ui/components/formElements/accessories/InputLabel'
import { InputValidationMessage } from '~ui/components/formElements/accessories/InputMessages'

import { PasswordInputElement } from './PasswordInputElement'

type TNewPasswordForm = { newPassword: string }

type TProps = NoChildren

/**
 * Password input used when the customer enters the new password.
 *
 * Displays all the rules demanded for the passwords (currently only the min length) with the validity indicator.
 * It is validated on change.
 *
 * ! Needs the `FormProvider` from 'react-hook-form' to be rendered somewhere above,
 * see {@link https://react-hook-form.com/api/formprovider} for examples and details.
 */
export const NewPasswordInput: React.FC<TProps> = () => {
  const { trans } = useTrans()
  const PASSWORD_RULES = {
    minLength: { value: 8, message: trans(msg`Password must have at least 8 characters`) },
  }

  const { control, trigger } = useFormContext<TNewPasswordForm>()

  const triggerField = React.useCallback(() => {
    return trigger(`newPassword`)
  }, [trigger])

  return (
    <InputControl isRequired>
      <InputLabel>
        <Trans>Enter your new password</Trans>
      </InputLabel>
      <Controller
        name={`newPassword`}
        control={control}
        rules={{
          ...PASSWORD_RULES,
          required: true,
          onChange: triggerField,
        }}
        render={({
          field: { onChange, onBlur, value, name },
          fieldState: { invalid, error, isDirty },
          formState: { isSubmitting },
        }) => (
          <>
            <PasswordInputElement
              id={name}
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              isInvalid={invalid}
              isDisabled={isSubmitting}
              autoComplete={`new-password`}
            />
            <AnimatedHeight isOpen={invalid || !isDirty}>
              {objectEntries(PASSWORD_RULES).map(([name, definition]) => {
                return (
                  <InputValidationMessage
                    key={name}
                    isValid={isValid({ error, definition, name, value })}
                  >
                    {definition.message}
                  </InputValidationMessage>
                )
              })}
            </AnimatedHeight>
          </>
        )}
      />
    </InputControl>
  )
}

//* HELPERS

type TOptions = {
  error: FieldError | undefined
  name: string
  definition: { value: ValidationValue; message: string }
  value: string
}

// decides if the individual password rule
// (from PASSWORD_RULES defined to be visible to the customer) is valid
const isValid = ({ error, definition, name, value }: TOptions) => {
  const isNotEmpty = value.length > 0

  if (!error && isNotEmpty) return true

  const hasErrorTypes = error?.types?.[name] !== undefined
  const isNotThisRuleError = error?.types?.[name] !== definition.message

  if (!hasErrorTypes) return false

  return isNotThisRuleError
}
